/**
 * MEDIEVAL CASTLE — map pass (2026-11-04).
 *
 * `build`: our own procedural ARCHITECTURE dressing (castleLayout.ts — owner
 * rule: castle architecture is built in code, real castles are reference only):
 * machicolation corbels under every wall walk and tower top, cross-loop arrow
 * slits in cut-stone surrounds, merlon coping, heraldic banners on the towers
 * and gatehouse, torch sconces at the gate. Four merged meshes + one banner
 * mesh for the whole castle, built once; no per-frame work.
 *
 * `propSwaps`: loose PROPS only become real, restyled models:
 *   - the farm HAYSTACKS (two stacked boxes → our stacked rick of square bales);
 *   - the market-square WELL (base + posts + roof slab → stone well, windlass,
 *     bucket — the same restyled model as the frontier town's).
 * Colliders untouched; matched by size/position.
 */
import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import type { MapBox, MapDef } from "@neon-purge/shared";
import type { MapExtrasContext } from "../mapExtras.js";
import { at, sizeIs, wellSwap, type PropSwap } from "../propSwapLayout.js";
import { castleDressing, type LBox } from "../castleLayout.js";
import { surfaceMaps, worldProjectUV } from "../mapTextures.js";

export const mapIds = ["medieval_castle"];

function boxesGeo(list: readonly LBox[], tint: THREE.Color | null, tile: number): THREE.BufferGeometry | null {
  if (!list.length) return null;
  const geos: THREE.BufferGeometry[] = [];
  for (const b of list) {
    const g = new THREE.BoxGeometry(b.s[0], b.s[1], b.s[2]);
    g.translate(b.p[0], b.p[1], b.p[2]);
    if (tile > 0) worldProjectUV(g, tile);
    if (tint) {
      const n = g.getAttribute("position").count, c = new Float32Array(n * 3);
      for (let i = 0; i < n; i++) {
        // a little per-stone value jitter so the corbel courses don't read as one extrusion
        const k = 0.92 + 0.12 * (((Math.sin(b.p[0] * 3.1 + b.p[1] * 7.7 + b.p[2] * 5.3) * 43758.5) % 1 + 1) % 1);
        c[i * 3] = tint.r * k; c[i * 3 + 1] = tint.g * k; c[i * 3 + 2] = tint.b * k;
      }
      g.setAttribute("color", new THREE.BufferAttribute(c, 3));
    }
    geos.push(g);
  }
  const m = mergeGeometries(geos);
  for (const g of geos) g.dispose();
  return m;
}

/** Our heraldry: teal field, gold border, oxide-red chevron between three gold
 *  roundels, swallow-tailed hem (alpha-cut). Drawn once per map load. */
function bannerTexture(): THREE.CanvasTexture {
  const c = document.createElement("canvas");
  c.width = 256; c.height = 512;
  const g = c.getContext("2d")!;
  g.clearRect(0, 0, 256, 512);
  g.beginPath();
  g.moveTo(0, 0); g.lineTo(256, 0); g.lineTo(256, 512); g.lineTo(128, 420); g.lineTo(0, 512); g.closePath();
  g.fillStyle = "#c9a646"; g.fill();                       // gold border
  g.beginPath();
  g.moveTo(14, 14); g.lineTo(242, 14); g.lineTo(242, 478); g.lineTo(128, 398); g.lineTo(14, 478); g.closePath();
  g.fillStyle = "#24505f"; g.fill();                       // teal field
  g.beginPath();                                            // chevron
  g.moveTo(14, 300); g.lineTo(128, 170); g.lineTo(242, 300); g.lineTo(242, 360); g.lineTo(128, 230); g.lineTo(14, 360); g.closePath();
  g.fillStyle = "#8e3a26"; g.fill();
  g.fillStyle = "#c9a646";
  for (const [x, y] of [[70, 100], [186, 100], [128, 330]] as const) { g.beginPath(); g.arc(x, y, 26, 0, Math.PI * 2); g.fill(); }
  // cloth: fine weave + soft vertical folds + grime toward the hem
  const img = g.getImageData(0, 0, 256, 512), d = img.data;
  for (let y = 0; y < 512; y++) for (let x = 0; x < 256; x++) {
    const i = (y * 256 + x) * 4;
    const fold = 0.86 + 0.14 * Math.sin(x / 256 * Math.PI * 5);
    const weave = ((x + y) & 1) ? 0.96 : 1.02;
    const grime = 1 - 0.18 * Math.max(0, y - 300) / 212;
    const k = fold * weave * grime;
    d[i] *= k; d[i + 1] *= k; d[i + 2] *= k;
  }
  g.putImageData(img, 0, 0);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 4;
  return t;
}

export function build(ctx: MapExtrasContext): THREE.Object3D | null {
  if (ctx.theme !== "day") return null;
  const L = castleDressing(ctx.map, ctx.lowSpec);
  const group = new THREE.Group();
  group.name = "castle-architecture";
  const add = (geo: THREE.BufferGeometry | null, mat: THREE.Material, cast: boolean): void => {
    if (!geo) { mat.dispose(); return; }
    geo.computeBoundingSphere();
    const m = new THREE.Mesh(geo, mat);
    m.castShadow = cast && ctx.shadows;
    m.receiveShadow = true;
    group.add(m);
  };
  add(boxesGeo(L.stone, new THREE.Color("#8d887d"), 2.4),
    new THREE.MeshStandardMaterial(surfaceMaps("trim", { color: "#ffffff", vertexColors: true, roughness: 0.95, metalness: 0.02 }, 1.1)), true);
  add(boxesGeo(L.slits, null, 0), new THREE.MeshStandardMaterial({ color: "#121110", roughness: 1, metalness: 0 }), false);
  add(boxesGeo(L.iron, null, 0), new THREE.MeshStandardMaterial({ color: "#2b2e33", roughness: 0.5, metalness: 0.8 }), true);
  add(boxesGeo(L.flames, null, 0), new THREE.MeshStandardMaterial({ color: "#ffb35a", emissive: "#ff7a1e", emissiveIntensity: 2.6, roughness: 1 }), false);
  // banners: one merged, gently bellied cloth mesh
  if (L.banners.length) {
    const geos: THREE.BufferGeometry[] = [];
    for (const b of L.banners) {
      const g = new THREE.PlaneGeometry(b.w, b.h, 4, 10);
      const pos = g.getAttribute("position");
      for (let i = 0; i < pos.count; i++) {
        const x = pos.getX(i), y = pos.getY(i);
        const t = 0.5 - y / b.h;                 // 0 top → 1 hem
        // belly: cloth bows out between its hanging rod and the hem, ≤ 7 cm
        pos.setZ(i, 0.07 * Math.sin(t * Math.PI) * (1 - 0.3 * (x / b.w) ** 2));
      }
      g.computeVertexNormals();
      g.rotateY(Math.atan2(b.nx, b.nz));
      g.translate(b.c[0], b.c[1], b.c[2]);
      geos.push(g);
      // the hanging rod (iron), as wide as the banner
      const rod = new THREE.CylinderGeometry(0.04, 0.04, b.w + 0.3, 6);
      rod.rotateZ(Math.PI / 2);
      rod.rotateY(Math.atan2(b.nx, b.nz));
      rod.translate(b.c[0], b.c[1] + b.h / 2 + 0.03, b.c[2]);
      geos.push(rod);
    }
    // planes and cylinders are both indexed → mergeable; the rods sample the
    // banner's gold border corner so one material serves both
    for (const g of geos) if (g.type === "CylinderGeometry") {
      const uv = g.getAttribute("uv"); for (let i = 0; i < uv.count; i++) uv.setXY(i, 0.02, 0.02);
    }
    const merged = mergeGeometries(geos);
    for (const g of geos) g.dispose();
    add(merged, new THREE.MeshStandardMaterial({
      map: bannerTexture(), alphaTest: 0.5, side: THREE.DoubleSide, roughness: 0.9, metalness: 0,
    }), true);
  }
  return group.children.length ? group : null;
}

export function propSwaps(map: MapDef): PropSwap[] {
  const B = map.boxes;
  const out: PropSwap[] = [];
  const used = new Set<MapBox>();
  for (const b of B) {
    if (used.has(b)) continue;
    let sw: PropSwap | null = null;
    if (sizeIs(b, 2.6, 1.7, 2.2) && (b.color ?? "").toLowerCase() === "#b09548") {
      const cap = B.find((c) => sizeIs(c, 1.6, 0.7, 1.3) && at(c, b.p[0], null, b.p[2]));
      if (cap) sw = { kind: "haystack", boxes: [b, cap], replace: [b, cap] };
    } else if (sizeIs(b, 2.4, 1.0, 2.4)) {
      sw = wellSwap(B, b); // farm wells have no posts/roof → null, keep their box
    }
    if (!sw || sw.boxes.some((x) => used.has(x))) continue;
    for (const x of sw.boxes) used.add(x);
    out.push(sw);
  }
  return out;
}
